import { Form } from "antd";
import { useState, useEffect } from "react";
import {
  deleteProgramCategory,
  updateProgramCategory,
  addProgramCategory,
} from "../repository/data.repository";
import { ProgramCategory } from "../repository/data.default";
import DynamicForm from "../controls/DynamicForm";
import { ProgramCategoriesForm } from "./ProgramCategoriesView.default";
import InModal from "../controls/InModal";
import { modalTitle } from "../controls/InModal.default";
import ModalFooter from "../controls/ModalFooter";
import ModalDeleteConfirmation from "../controls/ModalDeleteConfirmation";
import ModalErrorAlert from "../controls/ModalErrorAlert";
import { getProblemMessage } from "../utils/http";

export interface ProgramCategoryFormDialogProps {
  readonly open?: boolean;
  readonly mode: "add" | "update" | "delete";
  readonly initialValues?: ProgramCategory;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

function ProgramCategoryFormDialog(props: ProgramCategoryFormDialogProps) {
  const [form] = Form.useForm();
  const values = Form.useWatch<ProgramCategoriesForm>([], form);
  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (props.open) {
      form.setFieldsValue({ ...props.initialValues });
    } else {
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then((value) => setValid(!!Object.values(value).length))
      .catch(() => setValid(false));
  }, [form, values, props.open]);

  function onError(error: unknown): void {
    setLoading(false);
    setErrorMessage(getProblemMessage(error, "Something went wrong, try again"));
  }

  function onSubmit(): void {
    const { name, description } = values;
    setLoading(true);
    setErrorMessage(null);
    if (props.mode === "add") {
      addProgramCategory({ name, description })
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(onError);
    } else if (props.mode === "delete") {
      deleteProgramCategory(props.initialValues?.uid!)
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(onError);
    } else if (props.mode === "update") {
      updateProgramCategory({
        uid: props.initialValues?.uid,
        rowVersion: props.initialValues?.rowVersion,
        name,
        description,
      })
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(onError);
    } else {
      setLoading(false);
      props.onFinish?.();
    }
  }

  function reset(): void {
    setValid(false);
    setErrorMessage(null);
    setLoading(false);
    form.resetFields();
  }

  const schemaControls = [
    {
      type: "textbox" as const,
      name: "name",
      label: "Name",
      rules: [{ required: true, message: "Name is required" }],
    },
    {
      type: "textbox" as const,
      name: "description",
      label: "Description",
    },
  ];

  return (
    <InModal
      open={props.open}
      size={props.mode === "delete" ? "small" : "medium"}
      title={modalTitle(props.mode, "Program Category", props.initialValues?.name)}
      onCancel={props.onCancel}
      actions={
        <ModalFooter
          mode={props.mode}
          loading={loading}
          valid={valid}
          onCancel={props.onCancel}
          onSubmit={onSubmit}
        />
      }
    >
      {(props.mode === "add" || props.mode === "update") && (
        <DynamicForm
          className="w-auto"
          form={form}
          schema={{ groups: [{ controls: schemaControls }] }}
        />
      )}
      {props.mode === "delete" && (
        <ModalDeleteConfirmation
          entityLabel="program category"
          entityName={props.initialValues?.name}
        />
      )}
      <ModalErrorAlert message={errorMessage} />
    </InModal>
  );
}

export default ProgramCategoryFormDialog;
